import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { industryByKey } from "../lib/constants";
import { Plus, PencilSimple, Trash, FileArrowUp, Flask, Check, X } from "@phosphor-icons/react";
import Editor from "@monaco-editor/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "design", label: "Design Rules" },
  { key: "validation", label: "Validation Rules" },
  { key: "simulation", label: "Simulation Rules" },
  { key: "drawing", label: "Drawing Rules" },
  { key: "document", label: "Document Rules" },
];
const RULE_TYPES = [
  { key: "threshold", label: "Threshold (≤ / ≥ / =)" },
  { key: "range", label: "Range (min ≤ x ≤ max)" },
  { key: "formula", label: "Formula (Python expression)" },
  { key: "choice", label: "Choice (must equal)" },
  { key: "conditional", label: "Conditional (IF … THEN …)" },
];
const PRIORITIES = [
  { key: "override", label: "Override Standard", color: "var(--c-red)" },
  { key: "supplement", label: "Supplement Standard", color: "var(--c-amber)" },
  { key: "advisory", label: "Advisory Only", color: "var(--c-accent)" },
];
const SAFETY_PARAMS = new Set([
  "bil_hv_kvp", "bil_lv_kvp", "bil_neutral_kvp", "sil_hv_kvp", "sil_lv_kvp",
  "applied_voltage_kv", "induced_voltage_test", "pd_limit_pc", "insulation_class",
  "impedance_pct", "design_pressure", "design_pressure_tube", "design_pressure_shell",
  "breaking_capacity_ka", "corrosion_allowance", "joint_efficiency",
]);

const EMPTY_RULE = {
  rule_id: null, name: "", description: "", category: "design", industry_id: "all",
  type: "threshold", parameter: "", operator: "lte", value: "", min_value: "", max_value: "",
  formula_code: "", condition_code: "", priority: "supplement", active: true,
};

const OPSYM = { lte: "≤", gte: "≥", eq: "=" };

function valueText(r) {
  if (r.type === "threshold") return `${OPSYM[r.operator] || "?"} ${r.value}`;
  if (r.type === "range") return `${r.min_value} – ${r.max_value}`;
  if (r.type === "choice") return `= ${r.value}`;
  if (r.type === "formula") return `ƒ ${String(r.formula_code || "").slice(0, 42)}`;
  if (r.type === "conditional") return `IF ${String(r.condition_code || "").slice(0, 34)}…`;
  return "—";
}

export default function RulesEngine({ clientId, clientName }) {
  const { user } = useAuth();
  const orgIndustries = user?.org?.industries || [];
  const [rules, setRules] = useState([]);
  const [packs, setPacks] = useState([]);
  const [category, setCategory] = useState("design");
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [showExtract, setShowExtract] = useState(false);
  const [extractIndustry, setExtractIndustry] = useState("all");
  const [extracted, setExtracted] = useState(null);
  const [extractBusy, setExtractBusy] = useState(false);
  const [sourceDoc, setSourceDoc] = useState("");
  const [testResult, setTestResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [r, p] = await Promise.all([api.get(`/clients/${clientId}/rules`), api.get("/industry-packs")]);
      setRules(r.data);
      setPacks(p.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { if (clientId) load(); }, [clientId]);

  const catRules = rules.filter((r) => r.category === category);

  const paramOptions = useMemo(() => {
    const pack = packs.find((p) => p.key === draft.industry_id);
    if (!pack) return [];
    const fields = (pack.sections || []).flatMap((s) => s.fields.map((f) => ({ id: f.id, label: `${f.label} (${s.name})` })));
    const outputs = (pack.calc_modules || []).flatMap((m) => (m.outputs || []).map((o) => ({ id: o, label: `${o} (calc output · ${m.name})` })));
    return [...fields, ...outputs];
  }, [packs, draft.industry_id]);

  const openNew = () => { setDraft({ ...EMPTY_RULE, category }); setShowForm(true); };
  const openEdit = (r) => { setDraft({ ...EMPTY_RULE, ...r }); setShowForm(true); };

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Rule name required"); return; }
    const payload = { ...draft };
    delete payload.rule_id;
    try {
      const { data } = draft.rule_id
        ? await api.patch(`/rules/${draft.rule_id}`, payload)
        : await api.post(`/clients/${clientId}/rules`, payload);
      (data.warnings || []).forEach((w) => toast.warning(w, { duration: 6000 }));
      (data.conflicts || []).forEach((c) => toast.warning(`Rule conflict with "${c.name}": ${c.reason}`, { duration: 8000 }));
      toast.success(draft.rule_id ? "Rule updated" : "Rule created");
      setShowForm(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (r) => {
    try { await api.delete(`/rules/${r.rule_id}`); toast.success("Rule deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleActive = async (r) => {
    try { await api.patch(`/rules/${r.rule_id}`, { ...r, active: !r.active }); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const testRule = async (r) => {
    try {
      const { data } = await api.post("/rules/test", { ...r, client_id: clientId });
      setTestResult({ name: r.name, ...data });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const runExtract = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a PDF, DOCX or XLSX file"); return; }
    setExtractBusy(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("industry", extractIndustry);
      const { data } = await api.post(`/clients/${clientId}/rules/extract`, fd);
      setExtracted(data.rules);
      setSourceDoc(data.source_document);
      if (data.rules.length === 0) toast.info("No extractable rules found in the document");
    } catch (e) { toast.error(fmtErr(e), { duration: 8000 }); }
    finally { setExtractBusy(false); }
  };

  const acceptExtracted = async (r) => {
    try {
      await api.post(`/clients/${clientId}/rules`, {
        name: r.name, description: r.description, category: r.category, type: r.type,
        parameter: r.parameter, operator: r.operator, value: r.value,
        industry_id: extractIndustry, priority: "supplement", active: true,
        source_document: sourceDoc, source_clause: r.source_clause,
      });
      setExtracted((xs) => xs.filter((x) => x !== r));
      toast.success(`Accepted: ${r.name}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const prio = (k) => PRIORITIES.find((p) => p.key === k) || PRIORITIES[1];

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid="rules-engine">
      <div className="flex items-center gap-1 px-4 pt-3 border-b border-[var(--c-border)] flex-wrap">
        {CATEGORIES.map((c) => (
          <button key={c.key} data-testid={`rules-cat-${c.key}`} onClick={() => setCategory(c.key)}
            className={`px-3 py-2 text-xs border-b-2 transition-colors duration-150 ${
              category === c.key ? "border-[var(--c-accent)] text-[var(--c-text)]" : "border-transparent text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
            {c.label}
            <span className="ml-1.5 font-mono text-[10px] text-[var(--c-muted)]">{rules.filter((r) => r.category === c.key).length}</span>
          </button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" data-testid="upload-standards-btn" onClick={() => { setShowExtract(true); setExtracted(null); }}
          className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-7 text-xs mb-2 mr-2">
          <FileArrowUp size={13} className="mr-1" /> Upload Client Standards
        </Button>
        <Button size="sm" data-testid="add-rule-btn" onClick={openNew}
          className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-7 text-xs mb-2">
          <Plus size={13} className="mr-1" /> Add Rule
        </Button>
      </div>

      {testResult && (
        <div className={`mx-4 mt-3 px-3 py-2.5 rounded-sm border text-xs flex items-center gap-3 ${
          testResult.result === true ? "bg-[var(--c-green)]/10 border-[var(--c-green)]/40 text-[var(--c-text)]" :
          testResult.result === false ? "bg-[var(--c-red)]/10 border-[var(--c-red)]/40 text-[var(--c-text)]" :
          "bg-[var(--c-amber)]/10 border-[var(--c-amber)]/40 text-[var(--c-text)]"}`} data-testid="rule-test-result">
          <Flask size={14} />
          <span><b>{testResult.name}</b> — {testResult.detail}
            {testResult.project_code && <span className="text-[var(--c-muted)]"> · tested against <span className="font-mono">{testResult.project_code}</span></span>}
          </span>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider">
            {testResult.result === true ? "PASS" : testResult.result === false ? "FAIL" : "N/A"}
          </span>
          <button onClick={() => setTestResult(null)} className="text-[var(--c-muted)] hover:text-[var(--c-text)]"><X size={12} /></button>
        </div>
      )}

      <table className="w-full text-sm mt-1" data-testid="rules-table">
        <thead>
          <tr className="border-b border-[var(--c-border)]">
            {["Name", "Type", "Parameter", "Value", "Priority", "Active", ""].map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {catRules.map((r) => (
            <tr key={r.rule_id} data-testid={`rule-row-${r.rule_id}`}
              className="border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-border)]/20 transition-colors duration-150">
              <td className="px-4 py-2.5">
                <div className="text-xs font-medium text-[var(--c-text)]">{r.name}</div>
                <div className="text-[10px] text-[var(--c-muted)] truncate max-w-56">{r.description || r.source_document || ""}</div>
              </td>
              <td className="px-4 py-2.5"><span className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-muted)]">{r.type}</span></td>
              <td className="px-4 py-2.5 font-mono text-xs text-[var(--c-accent)]">{r.parameter || "—"}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-[var(--c-text)] max-w-44 truncate">{valueText(r)}</td>
              <td className="px-4 py-2.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm border whitespace-nowrap"
                  style={{ color: prio(r.priority).color, borderColor: `color-mix(in srgb, ${prio(r.priority).color} 25%, transparent)`, backgroundColor: `color-mix(in srgb, ${prio(r.priority).color} 10%, transparent)` }}>
                  {prio(r.priority).label}
                </span>
              </td>
              <td className="px-4 py-2.5">
                <Switch data-testid={`rule-active-${r.rule_id}`} checked={r.active} onCheckedChange={() => toggleActive(r)} />
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap text-right">
                <button data-testid={`rule-test-${r.rule_id}`} onClick={() => testRule(r)} title="Test rule against recent project data"
                  className="p-1 text-[var(--c-muted)] hover:text-[var(--c-green)] transition-colors duration-150"><Flask size={13} /></button>
                <button data-testid={`rule-edit-${r.rule_id}`} onClick={() => openEdit(r)}
                  className="p-1 text-[var(--c-muted)] hover:text-[var(--c-accent)] transition-colors duration-150"><PencilSimple size={13} /></button>
                <button data-testid={`rule-delete-${r.rule_id}`} onClick={() => remove(r)}
                  className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150"><Trash size={13} /></button>
              </td>
            </tr>
          ))}
          {catRules.length === 0 && (
            <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-[var(--c-muted)]" data-testid="rules-empty">
              No {CATEGORIES.find((c) => c.key === category)?.label.toLowerCase()} for {clientName}. Add one or upload client standards.
            </td></tr>
          )}
        </tbody>
      </table>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rule-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">{draft.rule_id ? "Edit Rule" : "Add Rule"}</DialogTitle>
            <DialogDescription className="sr-only">Define a client rule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Name *</Label>
                <Input data-testid="rule-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Category</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                  <SelectTrigger data-testid="rule-category" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Description</Label>
              <Input data-testid="rule-desc-input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Industry</Label>
                <Select value={draft.industry_id} onValueChange={(v) => setDraft({ ...draft, industry_id: v, parameter: "" })}>
                  <SelectTrigger data-testid="rule-industry" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    <SelectItem value="all" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">All Industries</SelectItem>
                    {orgIndustries.map((k) => <SelectItem key={k} value={k} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{industryByKey(k).name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Type</Label>
                <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v, value: "", min_value: "", max_value: "", formula_code: "", condition_code: "" })}>
                  <SelectTrigger data-testid="rule-type" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {RULE_TYPES.map((t) => <SelectItem key={t.key} value={t.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Parameter (from {draft.industry_id === "all" ? "free text" : "industry pack schema"})</Label>
              {draft.industry_id === "all" ? (
                <Input data-testid="rule-parameter" value={draft.parameter} onChange={(e) => setDraft({ ...draft, parameter: e.target.value })}
                  placeholder="e.g., rating_kva" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono text-xs" />
              ) : (
                <Select value={draft.parameter} onValueChange={(v) => setDraft({ ...draft, parameter: v })}>
                  <SelectTrigger data-testid="rule-parameter" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono text-xs"><SelectValue placeholder="Select parameter…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)] max-h-64">
                    {paramOptions.map((p) => <SelectItem key={p.id} value={p.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] font-mono text-xs">{p.id} <span className="text-[var(--c-muted)] font-sans">· {p.label}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            {draft.type === "threshold" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Operator</Label>
                  <Select value={draft.operator} onValueChange={(v) => setDraft({ ...draft, operator: v })}>
                    <SelectTrigger data-testid="rule-operator" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                      <SelectItem value="lte" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">≤ (at most)</SelectItem>
                      <SelectItem value="gte" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">≥ (at least)</SelectItem>
                      <SelectItem value="eq" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">= (exactly)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Value</Label>
                  <Input data-testid="rule-value" type="number" step="any" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
                </div>
              </div>
            )}
            {draft.type === "range" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Min</Label>
                  <Input data-testid="rule-min" type="number" step="any" value={draft.min_value} onChange={(e) => setDraft({ ...draft, min_value: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Max</Label>
                  <Input data-testid="rule-max" type="number" step="any" value={draft.max_value} onChange={(e) => setDraft({ ...draft, max_value: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
                </div>
              </div>
            )}
            {draft.type === "choice" && (
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Required value</Label>
                <Input data-testid="rule-value" value={String(draft.value ?? "")} onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
              </div>
            )}
            {draft.type === "formula" && (
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Python expression (must return True/False)</Label>
                <div className="border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="rule-formula-editor">
                  <Editor height="130px" language="python" theme="vs-dark" value={draft.formula_code}
                    onChange={(v) => setDraft({ ...draft, formula_code: v || "" })}
                    options={{ minimap: { enabled: false }, fontSize: 12, fontFamily: "JetBrains Mono", scrollBeyondLastLine: false }} />
                </div>
                <p className="text-[10px] text-[var(--c-muted)]">Spec parameters are in scope as variables, e.g. <span className="font-mono">impedance_pct &gt;= 6 and impedance_pct &lt;= 10</span></p>
              </div>
            )}
            {draft.type === "conditional" && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">IF (condition expression)</Label>
                  <div className="border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="rule-condition-editor">
                    <Editor height="90px" language="python" theme="vs-dark" value={draft.condition_code}
                      onChange={(v) => setDraft({ ...draft, condition_code: v || "" })}
                      options={{ minimap: { enabled: false }, fontSize: 12, fontFamily: "JetBrains Mono", scrollBeyondLastLine: false }} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">THEN (requirement expression, True/False)</Label>
                  <div className="border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="rule-then-editor">
                    <Editor height="90px" language="python" theme="vs-dark" value={draft.formula_code}
                      onChange={(v) => setDraft({ ...draft, formula_code: v || "" })}
                      options={{ minimap: { enabled: false }, fontSize: 12, fontFamily: "JetBrains Mono", scrollBeyondLastLine: false }} />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Priority</Label>
                <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
                  <SelectTrigger data-testid="rule-priority" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {draft.priority === "override" && SAFETY_PARAMS.has(draft.parameter) && (
                  <p className="text-[10px] text-[var(--c-red)]" data-testid="safety-note">Safety-critical parameter — cannot override standards; will be clamped to Supplement.</p>
                )}
              </div>
              <div className="flex items-center gap-2 pb-1">
                <Switch data-testid="rule-active-switch" checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
                <Label className="text-[var(--c-muted)] text-xs">Active</Label>
              </div>
            </div>
            <Button data-testid="rule-save-btn" onClick={save} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
              {draft.rule_id ? "Update Rule" : "Create Rule"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showExtract} onOpenChange={setShowExtract}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="extract-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">Upload Client Standards</DialogTitle>
            <DialogDescription className="sr-only">Extract rules from a client standards document with AI</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Document (PDF / DOCX / XLSX)</Label>
                <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx" data-testid="extract-file"
                  className="block w-full text-xs text-[var(--c-muted)] file:bg-[var(--c-border)] file:border-0 file:rounded-sm file:px-3 file:py-2 file:text-xs file:text-[var(--c-text)] file:mr-3" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Industry context</Label>
                <Select value={extractIndustry} onValueChange={setExtractIndustry}>
                  <SelectTrigger data-testid="extract-industry" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    <SelectItem value="all" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">All Industries</SelectItem>
                    {orgIndustries.map((k) => <SelectItem key={k} value={k} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{industryByKey(k).name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="extract-run-btn" onClick={runExtract} disabled={extractBusy}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
              {extractBusy ? "Extracting with Cognara AI…" : "Extract Rules with AI"}
            </Button>

            {extracted && (
              <div className="space-y-2" data-testid="extracted-rules">
                <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
                  {extracted.length} rules extracted from {sourceDoc} · review & accept
                </div>
                {extracted.map((r, i) => (
                  <div key={i} className="border border-[var(--c-border)] rounded-sm p-3 flex items-start gap-3" data-testid={`extracted-rule-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-[var(--c-text)]">{r.name}</div>
                      <div className="text-[11px] text-[var(--c-muted)] mt-0.5">{r.description}</div>
                      <div className="flex gap-2 mt-1.5 font-mono text-[10px] text-[var(--c-accent)] flex-wrap">
                        <span>{r.category}</span><span>{r.type}</span>
                        {r.parameter && <span>{r.parameter}</span>}
                        {r.value != null && <span>{OPSYM[r.operator]} {String(r.value)}</span>}
                        {r.source_clause && <span className="text-[var(--c-muted)]">§ {r.source_clause}</span>}
                      </div>
                    </div>
                    <button data-testid={`extract-accept-${i}`} onClick={() => acceptExtracted(r)}
                      className="p-1.5 rounded-sm bg-[var(--c-green)]/15 border border-[var(--c-green)]/40 text-[var(--c-green)] hover:bg-[var(--c-green)]/25" title="Accept rule"><Check size={13} weight="bold" /></button>
                    <button data-testid={`extract-reject-${i}`} onClick={() => setExtracted((xs) => xs.filter((x) => x !== r))}
                      className="p-1.5 rounded-sm bg-[var(--c-red)]/10 border border-[var(--c-red)]/40 text-[var(--c-red)] hover:bg-[var(--c-red)]/20" title="Reject"><X size={13} /></button>
                  </div>
                ))}
                {extracted.length === 0 && <div className="text-xs text-[var(--c-green)] py-2">All extracted rules reviewed.</div>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
